// Шаги обучения: что открыть, что подсветить и каким текстом объяснить.
//
// Как добавить шаг:
//   1. повесьте на элемент атрибут data-tour="имя" (см. ANCHORS ниже);
//   2. добавьте текст в locales/ru.js → steps.<ключ>;
//   3. впишите сюда строку { id, anchor, textKey } в нужном месте списка.
//
// Шаг, элемента которого нет на странице (нет прав на раздел, узкий экран),
// пропускается сам — проверять права здесь не нужно.

// Пункты чек-листа первых шагов, по порядку: путь менеджера до первой заявки.
export const CHECKLIST_ITEMS = ['profile', 'client', 'deal', 'application', 'decisions'];

// Имена якорей держим в одном месте: так видно, какие атрибуты data-tour
// должны стоять в разметке, и опечатка в селекторе не остаётся незамеченной.
export const ANCHORS = {
  sidebar: 'sidebar',
  menuClients: 'menu-clients',
  menuLeads: 'menu-leads',
  menuDeals: 'menu-deals',
  menuCars: 'menu-cars',
  menuProfile: 'menu-profile',
  tableSearch: 'table-search',
  tableFilters: 'table-filters',
  tableAdd: 'table-add',
  headerUser: 'header-user',
  sidebarHelp: 'sidebar-help',
};

export const selectorFor = (anchor) => `[data-tour="${anchor}"]`;

// Поля шага:
//   anchor    — что подсветить;
//   route     — какой раздел открыть перед показом. Менеджер видит живую
//               страницу со своими клиентами и сделками, а не слушает про неё
//               с чужого экрана;
//   requires  — по чему судить, нужен ли шаг вообще. У шага с переходом это
//               пункт меню: нет пункта — нет раздела, открывать нечего;
//   placement — желаемая сторона карточки.
const STEPS = [
  { id: 'welcome', anchor: null, textKey: 'welcome', placement: 'center' },
  { id: 'sidebar', anchor: ANCHORS.sidebar, textKey: 'sidebar', placement: 'right' },

  // Клиенты: открываем картотеку и на ней же разбираем то, что одинаково
  // во всех списках — кнопку добавления, быстрый поиск, отбор по условиям.
  {
    id: 'clients',
    anchor: ANCHORS.menuClients,
    route: '/',
    requires: ANCHORS.menuClients,
    textKey: 'clients',
    placement: 'right',
  },
  {
    id: 'addClient',
    anchor: ANCHORS.tableAdd,
    route: '/',
    requires: ANCHORS.menuClients,
    textKey: 'addClient',
    placement: 'bottom',
  },
  {
    id: 'search',
    anchor: ANCHORS.tableSearch,
    route: '/',
    requires: ANCHORS.menuClients,
    textKey: 'search',
    placement: 'bottom',
  },
  {
    id: 'filters',
    anchor: ANCHORS.tableFilters,
    route: '/',
    requires: ANCHORS.menuClients,
    textKey: 'filters',
    placement: 'bottom',
  },

  {
    id: 'leads',
    anchor: ANCHORS.menuLeads,
    route: '/leads',
    requires: ANCHORS.menuLeads,
    textKey: 'leads',
    placement: 'right',
  },
  {
    id: 'deals',
    anchor: ANCHORS.menuDeals,
    route: '/deals',
    requires: ANCHORS.menuDeals,
    textKey: 'deals',
    placement: 'right',
  },
  {
    id: 'createDeal',
    anchor: ANCHORS.tableAdd,
    route: '/deals',
    requires: ANCHORS.menuDeals,
    textKey: 'createDeal',
    placement: 'bottom',
  },
  {
    id: 'cars',
    anchor: ANCHORS.menuCars,
    route: '/cars',
    requires: ANCHORS.menuCars,
    textKey: 'cars',
    placement: 'right',
  },
  {
    id: 'addCar',
    anchor: ANCHORS.tableAdd,
    route: '/cars',
    requires: ANCHORS.menuCars,
    textKey: 'addCar',
    placement: 'bottom',
  },

  { id: 'credit', anchor: null, textKey: 'credit', placement: 'center' },
  { id: 'decisions', anchor: null, textKey: 'decisions', placement: 'center' },
  {
    id: 'profile',
    anchor: ANCHORS.menuProfile,
    requires: ANCHORS.sidebar,
    textKey: 'profile',
    placement: 'right',
  },
  {
    id: 'help',
    anchor: ANCHORS.sidebarHelp,
    requires: ANCHORS.sidebar,
    textKey: 'help',
    placement: 'right',
  },
];

const isVisible = (node) => {
  if (!node) return false;

  // Спрятанное совсем (чужая роль, печатная версия) подсвечивать нечем.
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }

  // Уехало за край экрана — так на телефоне стоит закрытое боковое меню:
  // элемент есть, но сдвинут влево за границу окна.
  const rect = typeof node.getBoundingClientRect === 'function' ? node.getBoundingClientRect() : null;
  if (rect && (rect.right < 0 || rect.bottom < 0 || rect.left > window.innerWidth || rect.top > window.innerHeight)) {
    return false;
  }

  return true;
};

// Шаг без якоря (рассказ без подсветки) показываем всегда. Шаг с переходом
// судим по пункту меню: подсвечиваемый элемент появится только после перехода,
// и проверять его на текущей странице бессмысленно.
export const isStepAvailable = (step, doc = document) => {
  const marker = step.requires || step.anchor;
  if (!marker) return true;

  return isVisible(doc.querySelector(selectorFor(marker)));
};

export const availableSteps = (doc = document) => STEPS.filter((step) => isStepAvailable(step, doc));

export default STEPS;
